/**
 * Customer Messaging Notes — controlled vocabularies (Context Engine).
 *
 * THE single authored copy of the note-type, visibility, signal-tag, and
 * MEDDPICC dimension vocabularies. Both frontends import these; the backend
 * core (apps/backend/services/accountNotesCore.js) is CommonJS and cannot
 * consume this ESM package at runtime, so it keeps a marked MIRROR — same
 * convention as quotes.js / discountBucketOf(). If you change anything here,
 * update the backend mirror and the enricher prompt
 * (apps/backend/services/accountNotesEnricher.js), then re-enrich with
 * scripts/account-notes-enrich.js --force.
 */

/** [value, label] pairs for the note-type chips/filters. */
export const NOTE_TYPE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['general', 'General'],
  ['meeting_note', 'Meeting'],
  ['positioning', 'Positioning'],
  ['competitive_intel', 'Competitive'],
  ['quote', 'Quote'],
  ['pain_point', 'Pain point'],
  ['opportunity', 'Opportunity'],
] as const;

/** Display label per note type, including types not offered as capture chips. */
export const NOTE_TYPE_LABEL: Readonly<Record<string, string>> = {
  general: 'General',
  meeting_note: 'Meeting',
  positioning: 'Positioning',
  competitive_intel: 'Competitive',
  quote: 'Quote',
  pain_point: 'Pain point',
  opportunity: 'Opportunity',
  solution_proposal: 'Solution',
};

export type NoteVisibility = 'private' | 'team' | 'public';

/** [value, label, hint] for the visibility chips. private = capture default. */
export const NOTE_VIS_OPTIONS: ReadonlyArray<readonly [NoteVisibility, string, string]> = [
  ['private', 'Private', 'Only you — raw capture'],
  ['team', 'Team', "This account's assigned AE/SC"],
  ['public', 'Public', 'Anyone who can see this account'],
] as const;

export const NOTE_VIS_LABEL: Readonly<Record<NoteVisibility, string>> = {
  private: 'Private',
  team: 'Team',
  public: 'Public',
};

/** Controlled signal-tag vocabulary applied by the nightly enricher. */
export const NOTE_SIGNALS: readonly string[] = [
  'OBJECTION', 'REQUIREMENT', 'GAP', 'FUNDING', 'TIMELINE',
  'STAKEHOLDER', 'COMPETITOR', 'TESTIMONIAL', 'RISK', 'WIN', 'ARCHITECTURE',
] as const;

/** [key, label] MEDDPICC dimensions mined per note (8 dims incl. paper). */
export const MEDDPICC_DIMS: ReadonlyArray<readonly [string, string]> = [
  ['metrics', 'Metrics'],
  ['economic_buyer', 'Econ Buyer'],
  ['decision_criteria', 'Criteria'],
  ['decision_process', 'Process'],
  ['paper_process', 'Paper'],
  ['pain', 'Pain'],
  ['champion', 'Champion'],
  ['competition', 'Competition'],
] as const;

/** Deal-signal play labels (services/dealSignalsCore.js play keys). */
export const DEAL_PLAY_LABEL: Readonly<Record<string, string>> = {
  'renewal-window': 'Renewal window',
  funding: 'Funding signal',
  'champion-pain-no-opp': 'Champion + pain, no opp',
  'objection-cluster': 'Objection cluster',
  'bcp-funding': 'BCP funding',
};
